import React, { useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { MdClose } from 'react-icons/md';
import FocusTrap from 'focus-trap-react';
import { IconButton } from '@material-ui/core';
import { Text, Stack, devices, BlueButtonBasic } from '../components/common';
import { ExternalLink, PhoneIcon, EmailIcon } from './SVG';
import { Employee } from '../@types/types';
import Markdown from './Markdown';
import { RichTextContent } from './StyledComponents';

type Props = {
  employeeData: Employee;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
};

const Modal = ({ employeeData, showModal, setShowModal }: Props) => {
  const {
    Name,
    Video: video,
    Picture,
    Details,
    Email: email,
    PhoneNumber: phone,
    ReservationLink: link,
    Title,
    videoThumbnail,
  } = employeeData;

  const modalRef = useRef();

  const closeModal = (e: any) => {
    if (modalRef.current === e.target) {
      setShowModal(false);
    }
  };

  const keyPress = useCallback(
    (e: any) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    },
    [setShowModal, showModal]
  );

  useEffect(() => {
    document.addEventListener('keydown', keyPress);
    return () => document.removeEventListener('keydown', keyPress);
  }, [keyPress]);

  const image = Picture?.publicURL;
  return (
    <>
      {showModal ? (
        <Background onClick={closeModal} ref={modalRef}>
          <FocusTrap>
            <ModalWrapper>
              <IconContainer>
                <CloseModalButton
                  aria-label="Close modal"
                  onClick={() => setShowModal(!showModal)}
                >
                  <CloseIcon />
                </CloseModalButton>
              </IconContainer>
              <ModalContent>
                <LeftContainer>
                  <ProfileContainer axis="y" spacing="none">
                    {image && <Image image={image} />}
                    <InfoContainer axis="y" spacing="default">
                      <Profile>
                        <NameText variant="subtitle">{Name}</NameText>
                        <TitleText variant="body">{Title}</TitleText>
                      </Profile>
                      <ContactContainer>
                        {phone && (
                          <Info>
                            <PhoneIcon />
                            <a
                              href={`tel:${phone}`}
                              style={{
                                textDecoration: 'none',
                                width: '100%',
                              }}
                            >
                              <InfoText variant="body">{phone}</InfoText>
                            </a>
                          </Info>
                        )}

                        {email && (
                          <Info>
                            <EmailIcon />
                            <InfoText variant="body">{email}</InfoText>
                          </Info>
                        )}
                      </ContactContainer>

                      {link && (
                        <Button href={link}>
                          Varaa aika
                          <CustomIcon fill="#fff" />
                        </Button>
                      )}
                    </InfoContainer>
                  </ProfileContainer>
                  {video?.publicURL && (
                    <VideoContainer>
                      <Text variant="body">Esittelyvideo</Text>
                      <Video>
                        <VideoPlayer
                          src={video.publicURL}
                          width="100%"
                          poster={videoThumbnail.Image.publicURL}
                          controls
                        />
                      </Video>
                    </VideoContainer>
                  )}
                </LeftContainer>

                <RightContainer>
                  <ContentContainer>
                    {Details && <Content width="95%">{Details}</Content>}
                  </ContentContainer>
                </RightContainer>
                {video?.publicURL && (
                  <VideoContainerMobile>
                    <Text variant="body">Esittelyvideo</Text>
                    <Video>
                      <VideoPlayer
                        src={video.publicURL}
                        width="100%"
                        controls
                      />
                    </Video>
                  </VideoContainerMobile>
                )}
              </ModalContent>
            </ModalWrapper>
          </FocusTrap>
        </Background>
      ) : null}
    </>
  );
};

export default Modal;

const Background = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  position: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: scroll;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 5;
`;

const ModalWrapper = styled.div`
  width: 1050px;
  max-height: 650px;
  box-shadow: 0 5px 16px rgba(0, 0, 0, 0.2);
  background: #fff;
  color: #000;
  position: relative;
  z-index: 10;
  border-radius: 10px;
  padding-bottom: 20px;

  @media ${devices.laptop} {
    width: 80%;
    margin: auto;
    overflow-y: auto;
    padding: 50px;
  }

  @media ${devices.mobileXL} {
    width: 90%;
    height: 85vh;
    padding: 0px;
  }
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  line-height: 1.8;
  color: #141414;
  padding: ${(props) => props.theme.spacing.xlarge}
    ${(props) => props.theme.spacing.medium};

  button {
    padding: 10px 24px;
    background: #141414;
    color: #fff;
    border: none;
  }

  @media ${devices.laptop} {
    flex-direction: column;
    margin-top: calc(-0.075 * 100vw);
  }

  @media ${devices.laptopXS} {
    margin-top: calc(-0.09 * 100vw);
  }

  @media ${devices.tablet} {
    margin-top: calc(-0.1 * 100vw);
  }

  @media ${devices.mobileXL} {
    margin-top: calc(-0.045 * 100vw);
  }
`;

const IconContainer = styled.div`
  width: 100%;
  height: 24px;
  padding: 15px 0px;
`;

const CloseModalButton = styled(IconButton)`
  && {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 32px;
    height: 32px;
    padding: 0;
    z-index: 10;

    &:focus-visible {
      ${(p) => p.theme.focus}
    }
  }
`;

const CloseIcon = styled(MdClose)`
  width: 32px;
  height: 32px;
  padding: 0;
  z-index: 10;
  fill: #000;
`;

const LeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;

  @media ${devices.laptop} {
    width: 100%;
  }
`;

const ProfileContainer = styled(Stack)`
  display: flex;
  flex-direction: row;

  @media ${devices.laptop} {
    width: 100%;
  }

  @media ${devices.mobileXL} {
    flex-direction: column;
    align-items: center;
  }
`;

const Image = styled.div<{ image: any }>`
  border-radius: 50%;
  min-height: 172px;
  max-height: 172px;
  min-width: 172px;
  max-width: 172px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url(${(props) => props.image});
`;

const Profile = styled.div``;

const VideoContainer = styled.div`
  margin-top: ${(props) => props.theme.spacing.medium};

  @media ${devices.laptop} {
    display: none;
  }
`;

const VideoContainerMobile = styled.div`
  display: none;

  @media ${devices.laptop} {
    display: block;
    margin: ${(props) => props.theme.spacing.medium} 0px
      ${(props) => props.theme.spacing.xxxlarge} 0px;
    padding-bottom: 50px 0px;
  }

  @media ${devices.mobileXL} {
    display: block;
    margin-top: ${(props) => props.theme.spacing.medium};
    margin-bottom: 0px;
  }
`;

const Video = styled.div`
  width: 380px;
  height: 225px;
  position: relative;

  @media ${devices.laptop} {
    width: 100%;
  }
`;

const VideoPlayer = styled.video`
  border-radius: 4px;
`;

const RightContainer = styled.div`
  width: 50%;

  @media ${devices.laptop} {
    margin-top: ${(props) => props.theme.spacing.medium};
    width: 100%;
  }
`;

const ContentContainer = styled.div`
  padding-top: ${(props) => props.theme.spacing.small};

  h3 {
    &:first-child {
      margin-top: 0px !important;
    }
  }

  @media (min-width: 1025px) {
    height: 530px;
    width: 98.3%;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
      height: 90%;
    }

    &::-webkit-scrollbar-track {
      -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 10px;
      -webkit-box-shadow: inset 0 0 6px #bababa;
    }
  }
`;

const Content = styled(RichTextContent)`
  margin-top: -15px;

  h3 {
    font-size: 18px;
    margin-bottom: 0px;
  }

  li {
    font-size: 18px;

    &:before {
      content: '- ';
      text-indent: -5px;
    }
  }
`;

const NameText = styled(Text)`
  display: block;
  font-size: ${(props) => props.theme.sizing.medium};
  margin-bottom: 4px;

  @media ${devices.mobileXL} {
    margin-top: ${(props) => props.theme.sizing.small};
    text-align: center;
  }
`;

const TitleText = styled(Text)`
  font-style: italic;
  margin-bottom: 0px;

  @media ${devices.mobileXL} {
    text-align: center;
  }
`;

const CustomIcon = styled(ExternalLink)`
  margin-left: 10px;
`;

const Button = styled(BlueButtonBasic)`
  width: fit-content;

  @media ${devices.mobileXL} {
    margin: ${(props) => props.theme.spacing.medium} auto 0px !important;
  }
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;

  p {
    margin-bottom: 0rem;
  }

  svg {
    max-width: 24px;
    max-height: 24px;
  }

  @media ${devices.mobileS} {
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: ${(props) => props.theme.spacing.small} 0px;
  }
`;

const InfoText = styled(Text)`
  margin-left: ${(props) => props.theme.spacing.default};
  word-wrap: break-word;
  width: calc(100% - 50px);
`;

const InfoContainer = styled(Stack)`
  width: 300px;
  padding-left: ${(props) => props.theme.spacing.medium};

  @media ${devices.laptop} {
    width: 100%;
  }

  @media ${devices.tablet} {
    align-items: inherit;
  }

  @media ${devices.mobileXL} {
    padding-left: 0px;
  }
`;
